import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, Popover, Calendar, Button,
} from 'antd';
import { CalendarOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';

const diaSeleccionadoStyle = {
  height: '100%',
  backgroundColor: '#52c41a33',
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
};

// Control interno: recibe value/onChange igual que cualquier control de
// antd Form (Form.Item se los inyecta automáticamente a su único hijo).
const MultiDatePickerControl = ({
  value, onChange, disabled, placeholder, format,
}) => {
  const [open, setOpen] = useState(false);
  const selectedDates = (value || [])
    .map((d) => moment(d))
    .sort((a, b) => a.valueOf() - b.valueOf());
  const [panelDate, setPanelDate] = useState(selectedDates[0] || moment());

  const toggleDate = (date) => {
    if (disabled) return;
    const exists = selectedDates.some((d) => d.isSame(date, 'day'));
    const next = exists
      ? selectedDates.filter((d) => !d.isSame(date, 'day'))
      : [...selectedDates, date.clone()];
    onChange(next.map((d) => d.format('YYYY-MM-DD')).sort());
  };

  return (
    <Popover
      trigger="click"
      open={!disabled && open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      // Evita que el popup se ancle mal cuando el control vive dentro de un
      // Modal. Se ancla al contenido del modal (un contenedor grande) en
      // vez de al div del propio trigger, para que Ant Design calcule bien
      // el espacio disponible y no "voltee" el popup de forma extraña.
      getPopupContainer={(trigger) => trigger.closest('.ant-modal-content') || trigger.parentElement}
      content={(
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px',
          }}
          >
            <Button
              type="link"
              size="small"
              disabled={!selectedDates.length}
              onClick={() => onChange([])}
            >
              Limpiar
            </Button>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
            />
          </div>
          <Calendar
            fullscreen={false}
            style={{ width: 280 }}
            value={panelDate}
            onSelect={toggleDate}
            onPanelChange={setPanelDate}
            disabledDate={() => disabled}
            dateCellRender={(cellValue) => (
              selectedDates.some((d) => d.isSame(cellValue, 'day'))
                ? <div style={diaSeleccionadoStyle}>{cellValue.date()}</div>
                : null
            )}
          />
        </div>
      )}
    >
      {/* Se envuelve en un div clickeable porque el onClick de Input solo
          queda enlazado al <input> nativo, no al ícono del suffix (un span
          hermano) -- así el clic en cualquier parte abre el popover. */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) setOpen(true);
        }}
      >
        <Input
          readOnly
          disabled={disabled}
          suffix={<CalendarOutlined />}
          placeholder={placeholder}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          value={selectedDates.map((d) => d.format(format)).join(', ')}
        />
      </div>
    </Popover>
  );
};

MultiDatePickerControl.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  format: PropTypes.string,
};

MultiDatePickerControl.defaultProps = {
  value: [],
  onChange: () => {},
  disabled: false,
  placeholder: '',
  format: 'DD/MM/YYYY',
};

// Componente público: se usa igual que NumericInput/PercentageInput, con su
// propio Form.Item ya envuelto (name/label/rules), como un DatePicker pero
// de selección múltiple no contigua.
const MultiDatePicker = ({
  name, label, rules, hasFeedback, disabled, placeholder, format, initialValue,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={rules}
    hasFeedback={hasFeedback}
    initialValue={initialValue}
  >
    <MultiDatePickerControl disabled={disabled} placeholder={placeholder} format={format} />
  </Form.Item>
);

MultiDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // eslint-disable-next-line react/forbid-prop-types
  rules: PropTypes.arrayOf(PropTypes.object),
  hasFeedback: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  format: PropTypes.string,
  initialValue: PropTypes.arrayOf(PropTypes.string),
};

MultiDatePicker.defaultProps = {
  label: '',
  rules: null,
  hasFeedback: false,
  disabled: false,
  placeholder: 'Seleccionar fecha(s)',
  format: 'DD/MM/YYYY',
  initialValue: [],
};

export default MultiDatePicker;
